import { useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import type {
  ConfigurationScope,
  ConfigurationScopeSummary,
  WriteConfigurationScopeRequest,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import { createRoute } from "../../routing/createRoute.js";
import { asChains, type Chain } from "./configurationChains.js";
import { ConfigurationScopeForm } from "./ConfigurationScopeForm.js";
import { writeScope, type ScopeWriteOutcome } from "./scopeWrites.js";

/**
 * How many configurations a chain shows before the rest are folded away.
 *
 * Enough for the top of a chain and its first branches, so a chain of dozens of
 * configurations does not push every other chain off the page.
 */
export const CHAIN_ROWS_SHOWN = 7;

const ChainTable = ({ chain }: { chain: Chain }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const headingId = `chain-${chain.top.id}`;
  const hidden = expanded ? 0 : chain.entries.length - CHAIN_ROWS_SHOWN;
  // Entries come top first, so cutting the list keeps the top of the chain.
  const shown =
    hidden > 0 ? chain.entries.slice(0, CHAIN_ROWS_SHOWN) : chain.entries;

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-labelledby={headingId}
      sx={{ borderLeft: 4, borderLeftColor: "primary.main" }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "baseline", flexWrap: "wrap", px: 2, py: 1.5 }}
      >
        <Typography id={headingId} variant="h6" component="h2">
          {t("admin.configuration.scope.chainTitle", { name: chain.top.name })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {[
            t("admin.configuration.scope.configurations", {
              count: chain.entries.length,
            }),
            // A single configuration has no levels worth counting.
            ...(chain.levels > 1
              ? [
                  t("admin.configuration.scope.levels", {
                    count: chain.levels,
                  }),
                ]
              : []),
            t("admin.configuration.scope.organisations", {
              count: chain.organisationCount,
            }),
          ].join(" · ")}
        </Typography>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              {t("admin.configuration.scope.configuration")}
            </TableCell>
            <TableCell sx={{ width: 120 }}>
              {t("admin.configuration.scope.level")}
            </TableCell>
            <TableCell sx={{ width: 200 }}>
              {t("admin.configuration.scope.usedBy")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell sx={{ pl: 2 + (entry.level - 1) * 3 }}>
                <Link
                  component={RouterLink}
                  to={createRoute.adminConfigurationScope(entry.id)}
                >
                  {entry.name}
                </Link>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={t("admin.configuration.scope.levelOf", {
                    level: entry.level,
                  })}
                  color={entry.level === 1 ? "primary" : "default"}
                  variant={entry.level === 1 ? "filled" : "outlined"}
                />
              </TableCell>
              <TableCell>
                {entry.organisationCount === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.configuration.scope.notUsedDirectly")}
                  </Typography>
                ) : (
                  <Link
                    component={RouterLink}
                    to={createRoute.adminOrganisationConfiguration(entry.id)}
                  >
                    {t("admin.configuration.scope.organisations", {
                      count: entry.organisationCount,
                    })}
                  </Link>
                )}
              </TableCell>
            </TableRow>
          ))}
          {hidden > 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Button
                  size="small"
                  onClick={() => {
                    setExpanded(true);
                  }}
                >
                  {t("admin.configuration.scope.showMore", { count: hidden })}
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};

export type ConfigurationChainsContentProps = {
  scopes: readonly ConfigurationScopeSummary[];
  loading: boolean;
  failed: boolean;
  onCreate: (
    body: WriteConfigurationScopeRequest,
  ) => Promisable<ScopeWriteOutcome>;
  /** Called once a new configuration exists, to open it. */
  onCreated: (scope: ConfigurationScope) => Promisable<void>;
};

/**
 * Every chain of configurations: what exists, what sits under what, and how many
 * organisations use each. A configuration's settings are on its own page.
 */
export const ConfigurationChainsContent = ({
  scopes,
  loading,
  failed,
  onCreate,
  onCreated,
}: ConfigurationChainsContentProps) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const chains = useMemo(() => asChains(scopes), [scopes]);

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          alignItems: { sm: "flex-start" },
          justifyContent: "space-between",
        }}
      >
        <Stack spacing={1} sx={{ maxWidth: 800 }}>
          <Typography variant="h5" component="h1">
            {t("admin.configuration.scopesTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("admin.configuration.scopesDescription")}
          </Typography>
        </Stack>
        <Box sx={{ flexShrink: 0 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={adding}
            onClick={() => {
              setAdding(true);
            }}
          >
            {t("admin.configuration.scope.add")}
          </Button>
        </Box>
      </Stack>

      {adding && (
        <Paper
          variant="outlined"
          component="section"
          aria-labelledby="new-configuration"
          sx={{ p: 2, borderColor: "primary.main" }}
        >
          <Stack spacing={2}>
            <Typography id="new-configuration" variant="h6" component="h2">
              {t("admin.configuration.scope.newTitle")}
            </Typography>
            <ConfigurationScopeForm
              scopes={scopes}
              editing={null}
              hint={t("admin.configuration.scope.newHint")}
              onSubmit={async (body) => {
                const outcome = await onCreate(body);

                await match(outcome)
                  .with({ kind: "saved" }, ({ scope }) => onCreated(scope))
                  .with({ kind: "refused" }, () => undefined)
                  .exhaustive();

                return outcome;
              }}
              onCancel={() => {
                setAdding(false);
              }}
            />
          </Stack>
        </Paper>
      )}

      {failed && (
        <Alert severity="error">
          {t("admin.configuration.scope.loadFailed")}
        </Alert>
      )}

      {loading && <CircularProgress size={20} />}

      {!loading && !failed && chains.length === 0 && (
        <Typography variant="body2">
          {t("admin.configuration.scope.empty")}
        </Typography>
      )}

      {chains.map((chain) => (
        <ChainTable key={chain.top.id} chain={chain} />
      ))}
    </Stack>
  );
};

/** The configuration chains, loaded and written through the API. */
export const AdminConfigurationScopesPage = () => {
  const navigate = useNavigate();
  const [{ data, loading, error }] = apiHooks.configuration.scopes();

  return (
    <ConfigurationChainsContent
      scopes={data?.scopes ?? []}
      loading={loading}
      failed={error !== undefined && error !== null}
      onCreate={(body) => writeScope({ scopeId: null, body })}
      onCreated={(scope) => {
        navigate(createRoute.adminConfigurationScope(scope.id));
      }}
    />
  );
};
